'use client';
import { useMediaQuery } from '@intigral/hooks';
import { Button, FontIcon, HeroBlock, LazyImage, MovieDetails, Skeleton, ImageContainer, Price } from '@intigral/shared';
import { PriceProps } from '@intigral/shared/lib/components/Price';
import { isValidProviderProp } from '@intigral/shared/components/MovieDetails';
import cn from 'classnames';
import React, { forwardRef, ReactNode, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MovieButtons } from '../MovieButtons';
import { MoviePreview } from '../MoviePreview';
import { useAnchoredPopup } from '../MoviePreview/useAnchoredPopup';
import styles from './movie.module.sass';

interface BaseProps
    extends React.HTMLAttributes<HTMLDivElement>,
        React.ComponentProps<typeof MovieButtons>,
        React.ComponentProps<typeof MovieDetails>,
        React.ComponentProps<typeof HeroBlock> {
    isFavourite?: boolean;
    onFavourite?: (next: boolean) => void;
    opinion?: 'like' | 'dislike' | null;
    onOpinion?: (next: 'like' | 'dislike' | null) => void;
    onMore?: React.MouseEventHandler<HTMLButtonElement>;
    muted?: boolean;
    onMute?: (next: boolean) => void;
    onFullscreen?: () => void;
    isLoading?: boolean;
}

interface LoadingProps extends BaseProps {
    isLoading: true;
    cover?: string;
    price?: string | PriceProps;
    timer?: React.ReactElement;
    setIsActive?: React.Dispatch<React.SetStateAction<boolean>>;
    icon?: 'remind' | 'locked';
    headingImage?: string;
    guid?: string;
    layout?: 'horizontal' | 'vertical' | 'vertical-2x' | 'default';
    rented?: boolean;
}

interface LoadedProps extends BaseProps {
    isLoading?: false;
    cover: string;
    price?: string | PriceProps;
    timer?: React.ReactElement;
    setIsActive?: React.Dispatch<React.SetStateAction<boolean>>;
    icon?: 'remind' | 'locked';
    guid?: string;
    layout?: 'horizontal' | 'vertical' | 'vertical-2x' | 'default';
    headingImage?: string;
    rented?: boolean;
}

type MovieCardProps = LoadingProps | LoadedProps;

export const MovieCard = forwardRef(
    (
        {
            cover,
            price,
            timer,
            setIsActive,
            icon,
            isLoading,
            layout = 'default',
            headingImage,
            rented = false,
            ...props
        }: MovieCardProps,
        ref
    ) => {
        const {
            play,
            progress,
            watchlist,
            watchlistButton,
            page,
            trailer,
            download,
            reminderButton,
            endActions,
            isFavourite,
            onFavourite,
            opinion,
            onOpinion,
            onMore,
            muted,
            onMute,
            onFullscreen,
            provider,
            locked,
            dolby,
            quality4K,
            onClickLock,
            rating,
            info,
            live,
            picture,
            heading,
            compact,
            minHeight,
            detail,
            theme,
            blurred,
            children,
            className,
            onClick,
            guid,
            ...restDivProps
        } = props;

        const movieDetailsProps = {
            provider,
            locked,
            dolby,
            quality4K,
            onClickLock,
            rating,
            info,
            live,
        };

        const isValidUrl = (str: string): boolean => {
            try {
                new URL(str);
                return true;
            } catch {
                return /^(\.\/|\/|data:)/.test(str);
            }
        };

        const queryClient = useQueryClient();

        const isMobile = useMediaQuery('(max-width: 924px)');

        const hasPopup = layout !== 'vertical-2x';

        const { cardRef, popupRef, showPopup, isOpen, setShowPopup, closePopup, handleMouseEnter, handleMouseLeave } =
            useAnchoredPopup({
                enabled: hasPopup && !isMobile,
                onHoverChange: (next) => {
                    if (!next && queryClient) {
                        queryClient.invalidateQueries({ queryKey: ['streamUrl'] });
                    }
                    setIsActive?.(next);
                },
            });

        useImperativeHandle(ref, () => ({
            showPopup: () => setShowPopup(true),
            hidePopup: () => closePopup(),
        }));

        const popupContent = (
            <MoviePreview
                ref={popupRef}
                className={cn(styles.popup, {
                    [styles.fadeIn]: showPopup,
                    [styles.fadeOut]: !showPopup,
                })}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                picture={picture ?? cover}
                heading={heading}
                headingImage={headingImage}
                play={play}
                reminderButton={reminderButton}
                watchlistButton={watchlistButton}
                endActions={endActions as ReactNode}
                isFavourite={isFavourite}
                onFavourite={onFavourite}
                opinion={opinion}
                onOpinion={onOpinion}
                onMore={onMore ?? page?.onClick}
                muted={muted}
                onMute={onMute}
                onFullscreen={onFullscreen}
                details={movieDetailsProps}
            />
        );

        if (isLoading) {
            return (
                <div className={cn(className, styles.card, styles[layout])} {...restDivProps}>
                    <Skeleton absolute />
                </div>
            );
        }

        return (
            <div
                {...restDivProps}
                ref={cardRef}
                className={cn(className, styles.card, styles[layout])}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={onClick}
                id={guid}
                data-test-id={guid}
            >
                <div className={styles.picture}>
                    <LazyImage src={cover} alt={typeof heading === 'string' ? heading : 'Movie poster image'} />
                    {/* <img src={cover} alt="" /> */}
                </div>

                {hasPopup &&
                    !isMobile &&
                    isOpen &&
                    (ReactDOM.createPortal(popupContent, document.body) as ReactNode)}
                {timer && timer}
                {price && (
                    <div className={cn(styles.rent, { [styles.rented]: rented })}>
                        {typeof price === 'string' && price}
                        {typeof price === 'object' && <Price {...price} />}
                    </div>
                )}
                {icon && (
                    <div className={cn(styles.icon, `${styles[icon]}`)}>
                        <FontIcon name={icon === 'remind' ? 'bell-filled' : 'lock'} size="x-small" md="smaller" />
                    </div>
                )}
                {layout === 'vertical-2x' && (
                    <>
                        {provider?.[0] && (
                            <div className={styles.provider}>
                                <ImageContainer src={provider[0]} />
                            </div>
                        )}
                        {headingImage && isValidUrl(headingImage) && (
                            <div className={styles.name}>
                                <ImageContainer src={headingImage} />
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }
);
